import {
    showConfirmMessage,
    showErrorMessage,
    showInfoMessage
} from '../../_shared/Messages/actions/MessageActions';
import messagesText from '../../_shared/Messages/texts';
import {approveWarning} from './services';
import {priority} from '@priority-software/priority-proto-api/lib';
import {MessageHandlers} from '../../services/proto/core/server';
import {APIActionStatus, formsStore} from '../../stores/Form';
import {restartProc} from '../../_shared/GWTBridge/invoke/genPage';
import {mapMessageData} from './utils';
import {EntityType} from '../../constants/EntityTypes';
import {texts} from '../Automations/texts';
import {clearFormEntity} from '../Form/actions/form';
import {getServerTextByKey} from '../../Localization/texts';
import {fatalSessionMsg} from '../../_legacy/Searches/consts/texts';

export const awaitingMessages: AwaitingMessage[] = [];

type AwaitingMessage = {
    response: any;
    session: string;
    handlers?: MessageHandlers;
};

type BasicMessageArgs = {Session?: string; Messages?: priority.netitems.shared.IMessage[]};

export let isMessageShown = false;
let messageStackIntervalId: ReturnType<typeof setInterval>;

const handleRecordUpdate = res => {
    const currentScreen = formsStore.currentScreen;

    if (res.Record) {
        const {currentRow, updateRecordValues} = currentScreen?.getActiveForm() || {};
        if (updateRecordValues) {
            updateRecordValues(currentRow, res.Record.Fields);
        }
    }
};

const continuePromiseStack = () => {
    messageStackIntervalId = setInterval(async () => {
        if (!awaitingMessages.length) {
            clearInterval(messageStackIntervalId);
            return;
        }

        if (awaitingMessages.length > 0 && !isMessageShown) {
            const {response, session} = awaitingMessages.pop();
            await handleMessage(response, session);
        }
    }, 200);
};

const pushMessageToStack = (message: AwaitingMessage) => {
    if (!awaitingMessages.length) {
        continuePromiseStack();
    }

    awaitingMessages.push(message);
};

export const handleMessage = async (
    response: BasicMessageArgs,
    session: string,
    handlers?: MessageHandlers,
    suppressErrors: boolean = false
): Promise<APIActionStatus> => {
    const messages: priority.netitems.shared.IMessage[] = response?.Messages || [];
    if (messages.length === 0) return Promise.resolve(APIActionStatus.passed);

    const messageType = messages[0]?.MType;
    return await handleMessageByType(response, session, messageType, handlers, suppressErrors);
};

export const showError = async (
    session: string,
    response: BasicMessageArgs,
    isCritical: boolean,
    handlers: MessageHandlers = {}
): Promise<APIActionStatus> => {
    const messages = response.Messages;
    const currentMessage = messages?.[0];
    const messagesData = mapMessageData(currentMessage);
    let fatalSessionFailure;

    if (isCritical && messagesData?.content.includes('session: failed to load session:')) {
        await getServerTextByKey(fatalSessionMsg).then(res => (fatalSessionFailure = res));
    }

    const alertErrorProps = !!currentMessage?.AlertNum
        ? {
              isAlertMessage: true,
              content: fatalSessionFailure || messagesData?.content,
              title: `${texts.business_rule_error_title} - ${currentMessage.AlertNum}`
          }
        : {};

    return await new Promise(resolve => {
        const handleCritical = async () => {
            await handlers?.onCriticalError?.();
            await clearFormEntity(session);
        };

        const props = {
            content: '',
            title: fatalSessionFailure || messagesData?.content,
            confirmButtonText: messagesText.accept,
            linkData: messagesData?.links,
            onLinkClick: async () => {
                const {linkData} = messagesData.links[0];
                if (linkData.eType == EntityType.PROCEDURE || linkData.eType == EntityType.REPORT) {
                    restartProc(linkData.eName, linkData.eType);
                    await handlers?.onErrorAccept?.();
                }
            },
            onConfirm: async () => {
                await handlers?.onErrorAccept?.();
                isMessageShown = false;
                resolve(APIActionStatus.blocked);
                if (isCritical) {
                    await handleCritical();
                }
            },
            onClose: async () => {
                await handlers?.onClose?.();
                isMessageShown = false;

                if (isCritical) {
                    await handleCritical();
                }
                resolve(APIActionStatus.blocked);
            },
            ...alertErrorProps
        };

        isMessageShown = true;
        showErrorMessage(props);
    });
};

const showWarning = async (
    response: BasicMessageArgs,
    session: string,
    handlers: MessageHandlers
): Promise<APIActionStatus> => {
    const messages: priority.netitems.shared.IMessage[] = response.Messages;
    return await new Promise(resolve => {
        const currentMsg = messages?.[0];

        const cancelHandler = async () => {
            isMessageShown = false;
            await handlers?.onClose?.();
            resolve(APIActionStatus.blocked);
        };

        const props = {
            content: '',
            defaultButton: 0,
            title: currentMsg?.Text,
            confirmButtonText: messagesText.yes,
            cancleButtonText: messagesText.cancel,
            onConfirm: async () => {
                isMessageShown = false;

                const approveWarningResponse = await approveWarning(session, {
                    handleMessages: false
                });
                handleRecordUpdate(approveWarningResponse);

                await handlers?.onWarningConfirm?.(approveWarningResponse);

                if (approveWarningResponse.Messages.length === 0) {
                    resolve(APIActionStatus.passed);
                } else {
                    const res = await handleMessage(approveWarningResponse, session, handlers);
                    resolve(res);
                }
            },
            onClose: cancelHandler,
            onCancel: cancelHandler
        };

        isMessageShown = true;
        showConfirmMessage(props);
    });
};

const showInfo = async (response, session, handlers): Promise<APIActionStatus> => {
    const messages: priority.netitems.shared.IMessage[] = response.Messages;
    const cb = () => {
        if (handlers?.onShowInfo) {
            handlers?.onShowInfo(response);
        } else {
            handleRecordUpdate(response);
        }
    };

    return await new Promise(resolve => {
        const currentMsg = messages?.[0];
        const props = {
            content: '',
            defaultButton: 0,
            title: currentMsg?.Text,
            confirmButtonText: messagesText.yes,
            onClose: () => {
                isMessageShown = false;
                cb();
                resolve(APIActionStatus.passed);
            }
        };

        isMessageShown = true;
        showInfoMessage(props);
    });
};

export const handleMessageByType = async (
    response: BasicMessageArgs,
    session: string,
    messageType: priority.netitems.shared.Message.Type,
    handlers?: MessageHandlers,
    skipErrors: boolean = false
): Promise<any> => {
    if (!isMessageShown) {
        const MessageType = priority.netitems.shared.Message.Type;
        const isCritical = messageType === MessageType.Fatal;

        if (messageType === MessageType.Error || isCritical) {
            if (skipErrors) {
                return APIActionStatus.blocked;
            }
            return await showError(session, response, isCritical, handlers);
        } else if (messageType === MessageType.Warning) {
            return await showWarning(response, session, handlers);
        } else if (messageType === MessageType.Information) {
            return await showInfo(response, session, handlers);
        }

        return Promise.resolve(APIActionStatus.blocked);
    } else {
        pushMessageToStack({response, session, handlers});
    }
};

export const showClientSideWarning = async (
    message: string,
    confirmCallback: () => void,
    cancelCallback: () => void
) => {
    const props = {
        content: message,
        title: '',
        confirmButtonText: messagesText.accept,
        cancleButtonText: messagesText.cancel,
        onConfirm: confirmCallback,
        onClose: cancelCallback,
        defaultButton: 0
    };

    showConfirmMessage(props);
};
